/**
 * Centralized language registry.
 *
 * Adding a language is a data change. The UI pickers, the translation engine,
 * the ASR hints, the TTS routing and the exporters all read from here.
 *
 * Provider-specific identifiers:
 *  - `translationName`  Workers AI `@cf/meta/m2m100-1.2b` expects a lowercased
 *                       English language *name*, not an ISO code.
 *  - `ttsCode`          Workers AI `@cf/myshell-ai/melotts` uses MeloTTS codes.
 *  - `asrCode`          BCP-47 hint for Deepgram / Whisper.
 */

import type { Language, LanguageCode } from "../types/language.js";

const L = (language: Language): Language => language;

export const LANGUAGES: readonly Language[] = [
  // ---- Germanic -------------------------------------------------------------
  L({
    code: "en",
    name: "English",
    nativeName: "English",
    region: "United States",
    regionCode: "US",
    locale: "en-US",
    baseCode: "en",
    direction: "ltr",
    family: "latin",
    translationName: "english",
    ttsCode: "EN",
    asrCode: "en-US",
    aliases: ["eng", "us english", "american english"],
  }),
  L({
    code: "en-GB",
    name: "English",
    nativeName: "English",
    region: "United Kingdom",
    regionCode: "GB",
    locale: "en-GB",
    baseCode: "en",
    direction: "ltr",
    family: "latin",
    translationName: "english",
    ttsCode: "EN",
    asrCode: "en-GB",
    aliases: ["british english", "uk english", "england"],
    note: "British",
  }),
  L({
    code: "de",
    name: "German",
    nativeName: "Deutsch",
    region: "Germany",
    regionCode: "DE",
    locale: "de-DE",
    baseCode: "de",
    direction: "ltr",
    family: "latin",
    translationName: "german",
    asrCode: "de-DE",
    aliases: ["ger", "deutsch"],
  }),
  L({
    code: "nl",
    name: "Dutch",
    nativeName: "Nederlands",
    region: "Netherlands",
    regionCode: "NL",
    locale: "nl-NL",
    baseCode: "nl",
    direction: "ltr",
    family: "latin",
    translationName: "dutch",
    asrCode: "nl-NL",
    aliases: ["nederlands", "flemish"],
  }),
  L({
    code: "af",
    name: "Afrikaans",
    nativeName: "Afrikaans",
    region: "South Africa",
    regionCode: "ZA",
    locale: "af-ZA",
    baseCode: "af",
    direction: "ltr",
    family: "latin",
    translationName: "afrikaans",
    asrCode: "af-ZA",
  }),
  // ---- Romance --------------------------------------------------------------
  L({
    code: "pt-BR",
    name: "Portuguese",
    nativeName: "Português (Brasil)",
    region: "Brazil",
    regionCode: "BR",
    locale: "pt-BR",
    baseCode: "pt",
    direction: "ltr",
    family: "latin",
    translationName: "portuguese",
    ttsCode: "BR",
    asrCode: "pt-BR",
    aliases: ["brazilian portuguese", "portugues", "portugues brasileiro", "brazil"],
    note: "Brazil",
  }),
  L({
    code: "pt-PT",
    name: "Portuguese",
    nativeName: "Português (Portugal)",
    region: "Portugal",
    regionCode: "PT",
    locale: "pt-PT",
    baseCode: "pt",
    direction: "ltr",
    family: "latin",
    translationName: "portuguese",
    ttsCode: "PT",
    asrCode: "pt-PT",
    aliases: ["european portuguese", "portugal"],
    note: "Portugal",
  }),
  L({
    code: "es",
    name: "Spanish",
    nativeName: "Español",
    region: "Spain",
    regionCode: "ES",
    locale: "es-ES",
    baseCode: "es",
    direction: "ltr",
    family: "latin",
    translationName: "spanish",
    ttsCode: "ES",
    asrCode: "es-ES",
    aliases: ["spa", "espanol", "castilian"],
  }),
  L({
    code: "es-MX",
    name: "Spanish",
    nativeName: "Español (México)",
    region: "Mexico",
    regionCode: "MX",
    locale: "es-MX",
    baseCode: "es",
    direction: "ltr",
    family: "latin",
    translationName: "spanish",
    ttsCode: "ES",
    asrCode: "es-MX",
    aliases: ["latin american spanish", "mexican spanish"],
    note: "Mexico",
  }),
  L({
    code: "fr",
    name: "French",
    nativeName: "Français",
    region: "France",
    regionCode: "FR",
    locale: "fr-FR",
    baseCode: "fr",
    direction: "ltr",
    family: "latin",
    translationName: "french",
    ttsCode: "FR",
    asrCode: "fr-FR",
    aliases: ["fre", "francais"],
  }),
  L({
    code: "it",
    name: "Italian",
    nativeName: "Italiano",
    region: "Italy",
    regionCode: "IT",
    locale: "it-IT",
    baseCode: "it",
    direction: "ltr",
    family: "latin",
    translationName: "italian",
    ttsCode: "IT",
    asrCode: "it-IT",
    aliases: ["ita", "italiano"],
  }),
  L({
    code: "ro",
    name: "Romanian",
    nativeName: "Română",
    region: "Romania",
    regionCode: "RO",
    locale: "ro-RO",
    baseCode: "ro",
    direction: "ltr",
    family: "latin",
    translationName: "romanian",
    asrCode: "ro-RO",
  }),
  L({
    code: "ca",
    name: "Catalan",
    nativeName: "Català",
    region: "Spain",
    regionCode: "ES",
    locale: "ca-ES",
    baseCode: "ca",
    direction: "ltr",
    family: "latin",
    translationName: "catalan",
    asrCode: "ca-ES",
  }),
  // ---- Slavic ---------------------------------------------------------------
  L({
    code: "ru",
    name: "Russian",
    nativeName: "Русский",
    region: "Russia",
    regionCode: "RU",
    locale: "ru-RU",
    baseCode: "ru",
    direction: "ltr",
    family: "cyrillic",
    translationName: "russian",
    asrCode: "ru-RU",
    aliases: ["rus"],
  }),
  L({
    code: "uk",
    name: "Ukrainian",
    nativeName: "Українська",
    region: "Ukraine",
    regionCode: "UA",
    locale: "uk-UA",
    baseCode: "uk",
    direction: "ltr",
    family: "cyrillic",
    translationName: "ukrainian",
    asrCode: "uk-UA",
  }),
  L({
    code: "pl",
    name: "Polish",
    nativeName: "Polski",
    region: "Poland",
    regionCode: "PL",
    locale: "pl-PL",
    baseCode: "pl",
    direction: "ltr",
    family: "latin",
    translationName: "polish",
    asrCode: "pl-PL",
  }),
  L({
    code: "cs",
    name: "Czech",
    nativeName: "Čeština",
    region: "Czechia",
    regionCode: "CZ",
    locale: "cs-CZ",
    baseCode: "cs",
    direction: "ltr",
    family: "latin",
    translationName: "czech",
    asrCode: "cs-CZ",
  }),
  // ---- East & Southeast Asian ---------------------------------------------
  L({
    code: "zh-Hans",
    name: "Mandarin Chinese",
    nativeName: "简体中文",
    region: "China",
    regionCode: "CN",
    locale: "zh-CN",
    baseCode: "zh",
    direction: "ltr",
    family: "cjk",
    translationName: "chinese (simplified)",
    ttsCode: "ZH",
    asrCode: "zh-CN",
    aliases: ["chinese", "simplified chinese", "mandarin", "zh-cn", "中文", "普通话"],
  }),
  L({
    code: "zh-Hant",
    name: "Chinese (Traditional)",
    nativeName: "繁體中文",
    region: "Taiwan",
    regionCode: "TW",
    locale: "zh-TW",
    baseCode: "zh",
    direction: "ltr",
    family: "cjk",
    translationName: "chinese (traditional)",
    ttsCode: "ZH",
    asrCode: "zh-TW",
    aliases: ["traditional chinese", "zh-tw", "cantonese"],
  }),
  L({
    code: "ja",
    name: "Japanese",
    nativeName: "日本語",
    region: "Japan",
    regionCode: "JP",
    locale: "ja-JP",
    baseCode: "ja",
    direction: "ltr",
    family: "cjk",
    translationName: "japanese",
    ttsCode: "JP",
    asrCode: "ja-JP",
    aliases: ["jpn"],
  }),
  L({
    code: "ko",
    name: "Korean",
    nativeName: "한국어",
    region: "South Korea",
    regionCode: "KR",
    locale: "ko-KR",
    baseCode: "ko",
    direction: "ltr",
    family: "cjk",
    translationName: "korean",
    ttsCode: "KR",
    asrCode: "ko-KR",
    aliases: ["kor"],
  }),
  L({
    code: "vi",
    name: "Vietnamese",
    nativeName: "Tiếng Việt",
    region: "Vietnam",
    regionCode: "VN",
    locale: "vi-VN",
    baseCode: "vi",
    direction: "ltr",
    family: "latin",
    translationName: "vietnamese",
    asrCode: "vi-VN",
  }),
  L({
    code: "th",
    name: "Thai",
    nativeName: "ไทย",
    region: "Thailand",
    regionCode: "TH",
    locale: "th-TH",
    baseCode: "th",
    direction: "ltr",
    family: "thai",
    translationName: "thai",
    asrCode: "th-TH",
  }),
  L({
    code: "id",
    name: "Indonesian",
    nativeName: "Bahasa Indonesia",
    region: "Indonesia",
    regionCode: "ID",
    locale: "id-ID",
    baseCode: "id",
    direction: "ltr",
    family: "latin",
    translationName: "indonesian",
    asrCode: "id-ID",
    aliases: ["bahasa indonesia"],
  }),
  L({
    code: "ms",
    name: "Malay",
    nativeName: "Bahasa Melayu",
    region: "Malaysia",
    regionCode: "MY",
    locale: "ms-MY",
    baseCode: "ms",
    direction: "ltr",
    family: "latin",
    translationName: "malay",
    asrCode: "ms-MY",
  }),
  L({
    code: "tl",
    name: "Tagalog",
    nativeName: "Tagalog",
    region: "Philippines",
    regionCode: "PH",
    locale: "tl-PH",
    baseCode: "tl",
    direction: "ltr",
    family: "latin",
    translationName: "tagalog",
    asrCode: "tl-PH",
    aliases: ["filipino"],
  }),
  // ---- Indic ---------------------------------------------------------------
  L({
    code: "hi",
    name: "Hindi",
    nativeName: "हिन्दी",
    region: "India",
    regionCode: "IN",
    locale: "hi-IN",
    baseCode: "hi",
    direction: "ltr",
    family: "indic",
    translationName: "hindi",
    ttsCode: "HI",
    asrCode: "hi-IN",
    aliases: ["hin"],
  }),
  L({
    code: "bn",
    name: "Bengali",
    nativeName: "বাংলা",
    region: "Bangladesh",
    regionCode: "BD",
    locale: "bn-BD",
    baseCode: "bn",
    direction: "ltr",
    family: "indic",
    translationName: "bangla",
    asrCode: "bn-BD",
    aliases: ["bangla", "bangladesh"],
  }),
  L({
    code: "ta",
    name: "Tamil",
    nativeName: "தமிழ்",
    region: "India",
    regionCode: "IN",
    locale: "ta-IN",
    baseCode: "ta",
    direction: "ltr",
    family: "indic",
    translationName: "tamil",
    asrCode: "ta-IN",
  }),
  L({
    code: "te",
    name: "Telugu",
    nativeName: "తెలుగు",
    region: "India",
    regionCode: "IN",
    locale: "te-IN",
    baseCode: "te",
    direction: "ltr",
    family: "indic",
    translationName: "telugu",
    asrCode: "te-IN",
  }),
  L({
    code: "mr",
    name: "Marathi",
    nativeName: "मराठी",
    region: "India",
    regionCode: "IN",
    locale: "mr-IN",
    baseCode: "mr",
    direction: "ltr",
    family: "indic",
    translationName: "marathi",
    asrCode: "mr-IN",
  }),
  L({
    code: "gu",
    name: "Gujarati",
    nativeName: "ગુજરાતી",
    region: "India",
    regionCode: "IN",
    locale: "gu-IN",
    baseCode: "gu",
    direction: "ltr",
    family: "indic",
    translationName: "gujarati",
    asrCode: "gu-IN",
  }),
  L({
    code: "pa",
    name: "Punjabi",
    nativeName: "ਪੰਜਾਬੀ",
    region: "India",
    regionCode: "IN",
    locale: "pa-IN",
    baseCode: "pa",
    direction: "ltr",
    family: "indic",
    translationName: "punjabi",
    asrCode: "pa-IN",
  }),
  // ---- Semitic & RTL --------------------------------------------------------
  L({
    code: "ar",
    name: "Arabic",
    nativeName: "العربية",
    region: "Saudi Arabia",
    regionCode: "SA",
    locale: "ar-SA",
    baseCode: "ar",
    direction: "rtl",
    family: "arabic",
    translationName: "arabic",
    asrCode: "ar-SA",
    aliases: ["ara"],
  }),
  L({
    code: "he",
    name: "Hebrew",
    nativeName: "עברית",
    region: "Israel",
    regionCode: "IL",
    locale: "he-IL",
    baseCode: "he",
    direction: "rtl",
    family: "hebrew",
    translationName: "hebrew",
    asrCode: "he-IL",
    aliases: ["iw", "hebr"],
  }),
  L({
    code: "fa",
    name: "Persian",
    nativeName: "فارسی",
    region: "Iran",
    regionCode: "IR",
    locale: "fa-IR",
    baseCode: "fa",
    direction: "rtl",
    family: "arabic",
    translationName: "persian",
    asrCode: "fa-IR",
    aliases: ["farsi", "per"],
  }),
  L({
    code: "ur",
    name: "Urdu",
    nativeName: "اردو",
    region: "Pakistan",
    regionCode: "PK",
    locale: "ur-PK",
    baseCode: "ur",
    direction: "rtl",
    family: "arabic",
    translationName: "urdu",
    asrCode: "ur-PK",
  }),
  // ---- Other ----------------------------------------------------------------
  L({
    code: "tr",
    name: "Turkish",
    nativeName: "Türkçe",
    region: "Türkiye",
    regionCode: "TR",
    locale: "tr-TR",
    baseCode: "tr",
    direction: "ltr",
    family: "latin",
    translationName: "turkish",
    asrCode: "tr-TR",
    aliases: ["turk"],
  }),
  L({
    code: "el",
    name: "Greek",
    nativeName: "Ελληνικά",
    region: "Greece",
    regionCode: "GR",
    locale: "el-GR",
    baseCode: "el",
    direction: "ltr",
    family: "greek",
    translationName: "greek",
    asrCode: "el-GR",
    aliases: ["ell", "hellenic"],
  }),
  L({
    code: "sv",
    name: "Swedish",
    nativeName: "Svenska",
    region: "Sweden",
    regionCode: "SE",
    locale: "sv-SE",
    baseCode: "sv",
    direction: "ltr",
    family: "latin",
    translationName: "swedish",
    asrCode: "sv-SE",
  }),
  L({
    code: "da",
    name: "Danish",
    nativeName: "Dansk",
    region: "Denmark",
    regionCode: "DK",
    locale: "da-DK",
    baseCode: "da",
    direction: "ltr",
    family: "latin",
    translationName: "danish",
    asrCode: "da-DK",
  }),
  L({
    code: "nb",
    name: "Norwegian",
    nativeName: "Norsk",
    region: "Norway",
    regionCode: "NO",
    locale: "nb-NO",
    baseCode: "nb",
    direction: "ltr",
    family: "latin",
    translationName: "norwegian",
    asrCode: "nb-NO",
    aliases: ["bokmal", "bokmål", "nor"],
  }),
  L({
    code: "fi",
    name: "Finnish",
    nativeName: "Suomi",
    region: "Finland",
    regionCode: "FI",
    locale: "fi-FI",
    baseCode: "fi",
    direction: "ltr",
    family: "latin",
    translationName: "finnish",
    asrCode: "fi-FI",
  }),
  L({
    code: "hu",
    name: "Hungarian",
    nativeName: "Magyar",
    region: "Hungary",
    regionCode: "HU",
    locale: "hu-HU",
    baseCode: "hu",
    direction: "ltr",
    family: "latin",
    translationName: "hungarian",
    asrCode: "hu-HU",
  }),
  L({
    code: "sw",
    name: "Swahili",
    nativeName: "Kiswahili",
    region: "Kenya",
    regionCode: "KE",
    locale: "sw-KE",
    baseCode: "sw",
    direction: "ltr",
    family: "latin",
    translationName: "swahili",
    asrCode: "sw-KE",
  }),
];

/** Sentinel for "detect the source language automatically". */
export const AUTO_LANGUAGE = "auto" as const;
export type AutoLanguage = typeof AUTO_LANGUAGE;

export const DEFAULT_SOURCE: LanguageCode = AUTO_LANGUAGE;
export const DEFAULT_TARGET: LanguageCode = "en";
export const FALLBACK_LANGUAGE: LanguageCode = "en";

const BY_CODE = new Map<LanguageCode, Language>(LANGUAGES.map((language) => [language.code, language]));

const BY_LOCALE = new Map<string, Language>();
const BY_BASE = new Map<string, Language[]>();
const BY_TRANSLATION_NAME = new Map<string, Language>();
const BY_TTS_CODE = new Map<string, Language>();

for (const language of LANGUAGES) {
  BY_LOCALE.set(language.locale.toLowerCase(), language);
  if (!BY_LOCALE.has(language.code.toLowerCase())) BY_LOCALE.set(language.code.toLowerCase(), language);
  if (!BY_LOCALE.has(language.baseCode.toLowerCase())) BY_LOCALE.set(language.baseCode.toLowerCase(), language);

  const siblings = BY_BASE.get(language.baseCode) ?? [];
  siblings.push(language);
  BY_BASE.set(language.baseCode, siblings);

  const key = language.translationName.toLowerCase();
  // Prefer the first (most canonical) registry entry for a shared model name.
  if (!BY_TRANSLATION_NAME.has(key)) BY_TRANSLATION_NAME.set(key, language);

  if (language.ttsCode) {
    const ttsKey = language.ttsCode.toLowerCase();
    if (!BY_TTS_CODE.has(ttsKey)) BY_TTS_CODE.set(ttsKey, language);
  }
}

for (const language of LANGUAGES) {
  for (const alias of language.aliases ?? []) {
    const key = alias.toLowerCase();
    if (!BY_LOCALE.has(key)) BY_LOCALE.set(key, language);
  }
  // Endonyms are lookup keys too: a detector or a caller may hand back
  // "Português" or "العربية" rather than a code. The parenthetical region is
  // indexed separately, because callers rarely include it.
  const native = language.nativeName.toLowerCase();
  const [base = native] = native.split(" (");
  for (const key of new Set([native, base.trim()])) {
    if (!BY_LOCALE.has(key)) BY_LOCALE.set(key, language);
    if (!BY_TRANSLATION_NAME.has(key)) BY_TRANSLATION_NAME.set(key, language);
  }
}

/** Exact registry lookup by canonical code. */
export function getLanguage(code: LanguageCode | null | undefined): Language | undefined {
  if (!code) return undefined;
  return BY_CODE.get(code);
}

/**
 * Lenient lookup. Accepts canonical codes, BCP-47 tags, ISO base codes, model
 * language names and aliases. Returns `undefined` when nothing matches, so
 * callers can decide whether to fall back or surface an "unsupported" state.
 */
export function resolveLanguage(input: string | null | undefined): Language | undefined {
  if (!input) return undefined;
  const raw = input.trim();
  if (!raw) return undefined;

  const direct = BY_CODE.get(raw);
  if (direct) return direct;

  const lowered = raw.toLowerCase();
  const localeHit = BY_LOCALE.get(lowered);
  if (localeHit) return localeHit;

  // `pt_BR`, `pt-br`, `pt BR` -> `pt-BR`
  const normalized = raw.replace(/_/g, "-").replace(/\s+/g, "-");
  const normalizedHit = BY_LOCALE.get(normalized.toLowerCase());
  if (normalizedHit) return normalizedHit;

  const base = lowered.split("-")[0];
  if (base) {
    const baseHit = BY_LOCALE.get(base);
    if (baseHit) return baseHit;
  }

  return BY_TRANSLATION_NAME.get(lowered);
}

/** Resolve, or return the fallback language. */
export function resolveLanguageOrFallback(input: string | null | undefined): Language {
  return resolveLanguage(input) ?? requireLanguage(FALLBACK_LANGUAGE);
}

/** Resolve or throw — for internal call sites where the code is known valid. */
export function requireLanguage(code: LanguageCode): Language {
  const language = BY_CODE.get(code);
  if (!language) {
    throw new Error(`Unknown language code: ${code}`);
  }
  return language;
}

export function isAutoLanguage(code: string | null | undefined): code is AutoLanguage {
  return code === AUTO_LANGUAGE;
}

export function listLanguages(): readonly Language[] {
  return LANGUAGES;
}

/** All variants sharing a base ISO code, canonical entry first. */
export function languageSiblings(code: LanguageCode): readonly Language[] {
  const language = getLanguage(code);
  if (!language) return [];
  return BY_BASE.get(language.baseCode) ?? [language];
}

/** The canonical (first-registered) entry for a base code. */
export function baseLanguage(code: LanguageCode): Language {
  const language = getLanguage(code);
  if (!language) return requireLanguage(FALLBACK_LANGUAGE);
  return (BY_BASE.get(language.baseCode) ?? [language])[0] ?? language;
}

/** BCP-47 tag to hand to speech recognition. */
export function toAsrCode(code: LanguageCode): string {
  const language = resolveLanguage(code);
  return language?.asrCode ?? language?.locale ?? FALLBACK_LANGUAGE;
}

/** Language name expected by `@cf/meta/m2m100-1.2b`. */
export function toTranslationName(code: LanguageCode): string {
  const language = resolveLanguage(code);
  return language?.translationName ?? FALLBACK_LANGUAGE;
}

/** Code expected by `@cf/myshell-ai/melotts`, or `null` when unsupported. */
export function toTtsCode(code: LanguageCode): string | null {
  return resolveLanguage(code)?.ttsCode ?? null;
}

/** Reverse lookup from a MeloTTS code back to a registry entry. */
export function fromTtsCode(ttsCode: string): Language | undefined {
  return BY_TTS_CODE.get(ttsCode.trim().toLowerCase());
}

export function toDirection(code: LanguageCode): "ltr" | "rtl" {
  return resolveLanguage(code)?.direction ?? "ltr";
}

export function toLocale(code: LanguageCode): string {
  return resolveLanguage(code)?.locale ?? FALLBACK_LANGUAGE;
}

/** "Português (Brasil)" — the label shown to users. */
export function languageLabel(code: LanguageCode): string {
  const language = resolveLanguage(code);
  if (!language) return code;
  return language.region ? `${language.nativeName} · ${language.region}` : language.nativeName;
}

/** "Portuguese" — English-only label, useful in dense UI. */
export function languageShortLabel(code: LanguageCode): string {
  const language = resolveLanguage(code);
  if (!language) return code;
  return language.note ? `${language.name} (${language.note})` : language.name;
}

export function supportsTts(code: LanguageCode): boolean {
  return toTtsCode(code) !== null;
}

export function supportsTranslation(code: LanguageCode): boolean {
  return resolveLanguage(code) !== undefined;
}
